import { Link } from "react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@taiga-platform/ui";

export function Faq() {
  return (
    <>
      <h1>FAQ</h1>

      <h2>The Team</h2>

      <Accordion type="multiple" className="not-prose my-4 text-sm">
        <AccordionItem value="team-different">
          <AccordionTrigger>
            What's different about this team?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                Not first-time founders. Built Cloud2 from zero to EUR 30M+, 150
                FTEs, PE exit — bootstrapped, profitable the entire way. 10+
                years working together across multiple companies. 80+ years
                combined tech leadership. 5 exits combined.
              </p>
              <p>
                The CEO spent nearly a decade paying back personal bankruptcy
                debts from his first company (Lehman crisis, age 22) — every
                cent, voluntarily. Then built Cloud2 as CTO for 8 years, serving
                as dual CISO/CIO, leading M&amp;A on both sides, and completing
                an Aalto EMBA. The governance expertise is not theoretical — it
                comes from years of being the person responsible for security,
                compliance, and risk at a 150-person company.
              </p>
              <p>
                What's new is venture capital for category-defining speed. The
                team has the enterprise DNA to sell into banks and defense
                contractors, the technical depth to build an AI governance
                platform, and the operational discipline to do both without
                burning cash. That combination is rare.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="team-bootstrap">
          <AccordionTrigger>
            Why not bootstrap like Cloud2?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                Could, and we know exactly how. But we are choosing velocity. The
                AI governance category is forming right now — 2025-2027 is the
                window. First to establish the standard wins. Bootstrap builds
                great companies. Venture builds category-defining ones. We have
                done the first. Now we are doing the second.
              </p>
              <p>
                The difference is timing. Cloud2 operated in a mature market
                (cloud consulting) where speed-to-market mattered less than
                execution quality. AI governance is a land grab — the regulatory
                tailwind (EU AI Act) and the enterprise adoption wave are
                happening simultaneously, and the window for an independent
                European player to own this layer is measured in months, not
                years.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="team-cloud2">
          <AccordionTrigger>
            What happened with Cloud2?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                Grew to EUR 30M+ revenue, 150 FTEs, bootstrapped and consistently
                profitable. Minority share exit to Private Equity. Company
                continues to operate successfully. Key accomplishments during
                the founders' tenure: PE exit, acquisition and integration of a
                Danish competitor, two SaaS products launched, ISO 27001
                certification, AWS Premier Partner status, Microsoft and Google
                strategic partnerships. We left on good terms to build something
                bigger.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="team-left-cloud2">
          <AccordionTrigger>
            Why did you leave Cloud2?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                To build something bigger. Cloud2 was a consulting company — a
                great one, but still fundamentally a services business where
                revenue scales with headcount. TAIGA is a product company where
                revenue scales with compute. The economics are different by an
                order of magnitude, and the opportunity — governing the entire
                enterprise AI software development lifecycle — is the kind of
                platform shift that only comes around once per generation.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="team-finland">
          <AccordionTrigger>Why Finland?</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                Finland leads EU GenAI adoption at 66% of firms. Deep engineering
                culture, strong AI research (Aalto, FCAI), and a natural home
                for EU-first technology companies. Finnish pragmatism — substance
                over style, direct communication, sisu — turns out to be exactly
                the right culture for building governance infrastructure. Nobody
                wants their governance layer built by people who over-promise
                and under-deliver.
              </p>
              <p>
                Practical advantages: Finnvera and Business Finland provide
                world-class non-dilutive funding instruments for startups. The
                talent pool punches above its weight — Finland produces more
                software engineers per capita than almost anywhere in Europe.
                And Helsinki is a 3-hour flight from every major European
                capital.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="team-four-founders">
          <AccordionTrigger>
            Four founders is a lot. How does decision-making work?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                Fast. We have worked together for over a decade. The trust and
                shorthand are already there — no "forming-storming-norming"
                phase. Each founder owns a domain end-to-end: Mikko (CEO) owns
                vision, sales, fundraising. Teemu (CTO) owns architecture and
                AI core. Henri (Chair/CCO) owns enterprise growth and strategic
                partnerships. Anton (CAIO) owns product and customer operations.
              </p>
              <p>
                Overlap is minimal. Disagreements are resolved in minutes, not
                weeks, because we have 10+ years of shared context for every
                decision. The working history table on the Team page shows the
                specifics: some pairs have worked together across two companies
                and nine years.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <h2>The Product</h2>

      <Accordion type="multiple" className="not-prose my-4 text-sm">
        <AccordionItem value="product-vs-devin">
          <AccordionTrigger>
            How is this different from Devin, Cursor, or Copilot?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                Those are coding tools for individuals. TAIGA is an enterprise
                platform governing the entire lifecycle. Like comparing a text
                editor to an ERP.
              </p>
              <p>
                Cursor makes a developer faster. TAIGA makes an organization's
                software development governed, auditable, and traceable to
                business intent. Different buyer (VP Eng / CISO, not individual
                developer), different problem (governance gap, not productivity
                gap), different product (full lifecycle platform, not IDE
                extension). We do not compete with Cursor — we govern the
                process that Cursor accelerates.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="product-lfa">
          <AccordionTrigger>
            What is LFA and why should I care?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                Logical Framework for Autonomous Action. Adapted from a
                methodology used by the World Bank and USAID since the 1960s
                for governing international development programs, brought to
                elite sports by Finnish F1 performance coach Aki Hintsa, and now
                applied to governing AI agents in software development.
              </p>
              <p>
                LFA uses two interconnected matrices: an upper matrix for
                business objectives ("customer churn drops 15%") and a lower
                matrix for software execution ("build self-service knowledge
                base"). The matrices connect at coupling points — business goals
                descend into technical work, and operational data rises back to
                verify whether the business goal was actually achieved.
              </p>
              <p>
                No competitor has anything like this. Most agent systems operate
                with implicit boundaries (prompt engineering) or no boundaries
                at all. TAIGA gives agents explicit, auditable autonomy
                boundaries backed by 60 years of proven methodology.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="product-compliance-layer">
          <AccordionTrigger>
            What is the Compliance Foundation Layer?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                The fastest path to value. Before deploying the full TAIGA
                platform, customers can start with the Compliance Foundation
                Layer — their policies (privacy policy, security standards,
                architectural rules) ingested into TAIGA's policy engine and
                exposed as an MCP server that plugs into existing development
                tools.
              </p>
              <p>
                A developer is vibe-coding with Claude Code and tries to add a
                LinkedIn URL field to a registration form. The compliance check
                fires automatically, cross-references against the company's
                privacy policy, and flags that social media profiles are not a
                declared data category. The violation is caught at the moment
                of creation — not during code review, not during an audit, not
                after a GDPR complaint.
              </p>
              <p>
                Zero disruption to existing workflows. Immediate ROI. And the
                gateway to full TAIGA adoption.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="product-open-source">
          <AccordionTrigger>
            What about open-source alternatives?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                Open-source AI coding tools are excellent for individual
                productivity. They do not solve enterprise governance. No
                open-source project provides policy enforcement, audit trails,
                compliance mapping, or business-goal traceability across the
                full development lifecycle.
              </p>
              <p>
                TAIGA uses open-source components where appropriate (React,
                Fastify, PostgreSQL, Terraform) but the governance layer — LFA
                framework, policy engine, discovery pipeline, council pattern,
                eval harness — is proprietary. The moat is not code, it is
                accumulated governance knowledge from every customer engagement.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="product-shadow-ai">
          <AccordionTrigger>
            What is Shadow AI and how does TAIGA address it?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                40% of enterprise developers use unsanctioned AI tools (Gartner
                2024). That is Shadow AI — AI-generated code entering production
                without organizational governance, visibility, or compliance
                checks. It is the #5 blocker to enterprise AI adoption, and it
                is getting worse as tools get better and easier to use.
              </p>
              <p>
                TAIGA addresses this by providing a governed alternative that is
                fast enough that developers actually want to use it. If the
                governed path is slower or more painful than the ungoverned
                path, developers will route around it every time. TAIGA makes
                governance invisible to the developer experience — compliance
                happens structurally, not as friction.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="product-self-host">
          <AccordionTrigger>
            Can customers self-host or deploy on-premises?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                AWS EU-hosted is the default. For defense and highly regulated
                customers, custom deployment options (VPC, dedicated tenancy,
                air-gapped) are on the roadmap. The architecture is designed for
                this from day one — multi-account isolation,
                infrastructure-as-code, no hard dependencies on shared
                infrastructure, and EU data residency enforced at the
                infrastructure level.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="product-llms">
          <AccordionTrigger>What LLMs does TAIGA use?</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                Anthropic Claude (primary) and OpenAI GPT-4o (council) via AWS
                Bedrock. The architecture is model-agnostic — swapping or adding
                models requires configuration, not code changes. The LLM Council
                pattern uses multiple models for cross-review on high-stakes
                generation (backlogs, PRDs), catching blind spots that any
                single model would miss.
              </p>
              <p>
                As models improve, TAIGA gets better without code changes. As
                costs decline (50%+ YoY), margins improve automatically. The
                platform is not a bet on any single model provider.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <h2>The Business</h2>

      <Accordion type="multiple" className="not-prose my-4 text-sm">
        <AccordionItem value="biz-outcome-pricing">
          <AccordionTrigger>
            How does outcome-based pricing work?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                Pay for deliverables, not hours. The governance engine (LFA/PRD)
                serves as the contract — both parties agree on scope, acceptance
                criteria, and price before work begins. AI agents execute within
                governance boundaries. Payment triggers on delivery against
                agreed criteria.
              </p>
              <p>
                No scope creep, no billing disputes. If TAIGA does not deliver,
                customers do not pay. This inverts the incentive structure of
                traditional consulting, where slower delivery means more
                revenue. At TAIGA, faster delivery means more capacity for the
                next customer.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="biz-use-of-funds">
          <AccordionTrigger>
            What will you do with the money?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                Product development (50-60%), customer deliveries (15-20%),
                first key hires (10-15%), operations (5-10%), buffer (5-10%).
                The first hire is already in progress — a full-stack AI builder
                to accelerate platform development. Every subsequent hire
                follows the same philosophy: minimize the need with AI first,
                then hire an industry leader who delivers a full team's
                execution power.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="biz-break-even">
          <AccordionTrigger>When do you break even?</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                Q3 2027. EBITDA positive by Q4 2027. The model achieves
                profitability at approximately EUR 500k/month revenue with
                approximately 20 people — reachable by Month 24. Bootstrap DNA
                means we know how to operate lean if timelines slip.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="biz-next-round">
          <AccordionTrigger>When's the next round?</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                Seed targeted Winter 2026-27, positioned by pilot traction and
                referenceable case studies. Target: EUR 10M at EUR 100M
                valuation. Milestones: market launch, international expansion,
                EUR 8M ARR run rate.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="biz-moat">
          <AccordionTrigger>What's the moat?</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>Three layers.</p>
              <p>
                <strong>The governance library.</strong> Accumulated Policy
                Cards, compliance mappings, validated workflows, and
                architectural patterns from every engagement. This is a data
                flywheel: each project makes the next one faster, cheaper, and
                more accurate. Competitors would need to deliver hundreds of
                governed projects across multiple regulated industries to
                replicate what TAIGA builds with every customer.
              </p>
              <p>
                <strong>The framework.</strong> LFA is not something you can
                bolt onto a coding assistant. It requires two-matrix
                architecture, policy engines, hierarchical governance, and
                structural enforcement — not prompt engineering.
              </p>
              <p>
                <strong>The relationships.</strong> 10+ years of enterprise
                sales experience and existing relationships in defense,
                healthcare, and finance. Enterprise deals close on trust, and
                trust is built over years of delivery.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="biz-why-outcome">
          <AccordionTrigger>
            Why outcome pricing instead of SaaS subscriptions?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                In Phase 1, outcome pricing proves the model: TAIGA only gets
                paid when it delivers value. That alignment is powerful for
                early enterprise sales — it eliminates procurement friction
                because the customer bears zero delivery risk. "Try us. If we
                don't deliver, you don't pay." That is a conversation no
                consultant can match.
              </p>
              <p>
                As the platform matures (Phase 2), we add SaaS subscriptions
                with token-based usage and platform burn credits. Phase 3
                combines full OaAS at scale with a partner ecosystem. The
                pricing model evolves with product maturity — each phase is
                natural, not forced.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <h2>Compliance &amp; Security</h2>

      <Accordion type="multiple" className="not-prose my-4 text-sm">
        <AccordionItem value="compliance-gdpr">
          <AccordionTrigger>
            How does TAIGA handle GDPR?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                EU data residency enforced at the infrastructure level via AWS
                SCPs — not a policy setting, an architectural constraint. Data
                physically cannot leave EU regions. DPIA completed. All
                processing documented. Data subject rights (access, erasure,
                portability) designed into the platform from day one.
              </p>
              <p>
                DPAs in place with all LLM providers prohibiting training on
                customer data. European data processing only. Right to erasure
                fully supported. Human gates on automated decisions satisfy
                GDPR Article 22. The team led GDPR compliance at Cloud2 — this
                is not new territory.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="compliance-eu-ai-act">
          <AccordionTrigger>What about the EU AI Act?</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                TAIGA's architecture is designed for EU AI Act compliance from
                the ground up. Audit trails, human oversight gates, technical
                documentation, transparency, and accountability chains are
                structural features — not afterthoughts bolted on for
                compliance.
              </p>
              <p>
                The Act becomes enforceable August 2026, which aligns precisely
                with TAIGA's first pilot deployments. European enterprises face
                a choice: bolt compliance onto ungoverned tools after the fact,
                or adopt a governance-first platform where compliance is a
                byproduct of normal operation. The Brussels Effect means this
                is not just a European story — multinationals will adopt
                EU-compliant standards globally.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="compliance-iso27001">
          <AccordionTrigger>
            Is TAIGA ISO 27001 certified?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                Not yet, but closer than you would expect at pre-seed. The team
                built and maintained ISO 27001 at Cloud2 over multiple years.
                TAIGA has already implemented an AI Agent-driven ISO
                27001-compliant ISMS for development and operations, and is
                considering early certification within months.
              </p>
              <p>
                Security documentation completed to date: STRIDE threat model
                (22 threats identified and mitigated), DPIA, data flow diagram,
                risk register (7 named platform risks), 16 formal security
                policies, incident response plan, architecture security review
                (6 trust boundaries). This is the security posture of a Series
                B company, built before the pre-seed closes.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <h2>Risk &amp; Strategy</h2>

      <Accordion type="multiple" className="not-prose my-4 text-sm">
        <AccordionItem value="risk-biggest">
          <AccordionTrigger>
            What are the biggest risks?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                We document them openly in the{" "}
                <Link to="/governance">Governance</Link> section. Top risks:
                well-funded competitor entering the governance space (medium
                likelihood, medium impact), slower-than-expected enterprise AI
                adoption (low likelihood, high impact), and LLM output quality
                for complex tasks (medium likelihood, medium impact).
              </p>
              <p>
                The team's enterprise background means we know how to sell into
                long procurement cycles and survive them. The bootstrap DNA
                means we know how to operate if timelines slip. And the
                regulatory tailwind (EU AI Act) creates mandatory demand that
                is independent of market sentiment.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="risk-hyperscaler">
          <AccordionTrigger>
            What if a hyperscaler builds this?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                AWS, Google, and Microsoft are all adding agentic capabilities
                to their platforms. None of them have governance-first
                architecture, outcome pricing, or full lifecycle coverage.
                Hyperscalers provide infrastructure — they do not deliver
                governed business outcomes. It is the same reason Salesforce
                exists despite Oracle having a database: different layer,
                different buyer, different value proposition.
              </p>
              <p>
                If anything, hyperscaler investment in agentic AI validates the
                market and increases demand for the governance layer that sits
                on top. More autonomous AI agents means more governance demand,
                not less.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="risk-bubble">
          <AccordionTrigger>
            What if the AI bubble pops?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                Then the companies that survive will be the ones with paying
                customers, real revenue, and capital efficiency. TAIGA has all
                three. Bootstrap DNA means we know how to operate lean. Paying
                customers from month one means we are not burning cash on hope.
                And governance becomes more important in a downturn, not less —
                when budgets tighten, enterprises cannot afford ungoverned AI
                creating compliance liability. The survivors will be the
                governed ones.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="risk-outcome-scale">
          <AccordionTrigger>
            Does outcome pricing actually scale?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                It does not — at least not in its pure form. And we know that.
                Phase 1 uses fixed MRR with outcome-based pilots because it
                proves the model and aligns incentives at small scale: if we
                do not deliver, customers do not pay. That builds trust and
                references faster than any SaaS subscription.
              </p>
              <p>
                But pure outcome pricing requires custom scoping for every
                deal, which is inherently services-like. That is why Phase 2
                transitions to SaaS tiered pricing with token-based usage and
                platform credits — a model that scales without custom scoping
                per customer. Phase 3 combines OaAS at scale with a partner
                ecosystem.
              </p>
              <p>
                The 3-phase model is designed to evolve the pricing as the
                product matures. We are not betting on outcome pricing forever.
                We are using it as a wedge to prove the value proposition and
                build the governance library, then transitioning to a scalable
                model once the product is mature enough for self-service.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="risk-lfa-demand">
          <AccordionTrigger>
            What if enterprises do not actually want LFA governance?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                They do not ask for LFA by name. Nobody walks into a meeting
                and says "I need a two-matrix Logical Framework for Autonomous
                Action." What they ask for is audit trails, policy enforcement,
                compliance mapping, and traceability from business objectives
                to deployed code.
              </p>
              <p>
                LFA is how TAIGA delivers those things structurally. The
                framework is invisible to the end user — developers experience
                governed AI development, not a methodology. The buyer (VP Eng,
                CISO) sees compliance and auditability. The developer sees an
                AI tool that works within boundaries. Neither needs to know the
                governance framework is called LFA.
              </p>
              <p>
                The risk is not that enterprises do not want governance. The
                risk is that they settle for weaker governance bolted onto
                existing tools. TAIGA's bet is that regulated industries —
                defense, healthcare, finance — need structural governance, not
                checkbox governance, and are willing to pay for the difference.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="risk-ai-act-timing">
          <AccordionTrigger>
            What about the EU AI Act timing? Can you ship before enforcement?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                The Compliance Foundation Layer is deployable today. It plugs
                into existing development tools as an MCP server — no platform
                migration, no retraining, no multi-month implementation.
                Companies can be compliance-ready before the August 2026
                enforcement deadline using existing tools plus TAIGA's policy
                engine.
              </p>
              <p>
                The full platform (Learn-Build-Run loops, agent orchestration,
                self-service portal) matures on a longer timeline — Phase 2
                targets M12-M24. But the critical insight is that compliance
                does not require the full platform. It requires the policy
                engine. And the policy engine ships first.
              </p>
              <p>
                This is deliberate. The wedge product addresses the immediate
                regulatory pressure. The full platform captures the long-term
                market opportunity. Both are needed, but they ship on different
                timelines.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <h2>The Fun Part</h2>

      <Accordion type="multiple" className="not-prose my-4 text-sm">
        <AccordionItem value="fun-ai-wrote">
          <AccordionTrigger>
            Did AI write this data room?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                Yes. TAIGA used AI agents (Claude Code) to build this entire
                data room — content, design, deployment, GitHub Pages
                configuration, everything. In a single working session. The
                irony is not lost on us.
              </p>
              <p>
                The fact that you are reading a well-structured, compliant,
                professionally designed data room that was built by AI agents
                is itself a demonstration of the thesis. Now imagine that same
                process with governance, audit trails, and policy enforcement
                at every step. That is the product.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="fun-name-meaning">
          <AccordionTrigger>What does TAIGA mean?</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                The taiga is the world's largest land biome — a vast boreal
                forest that stretches across the northern hemisphere. It is
                resilient, vast, and quietly essential. It also happens to be
                Finnish.
              </p>
              <p>
                We liked the metaphor: governance infrastructure that is
                everywhere, essential, and operates quietly in the background
                while everything else runs on top of it. Also, tai.ga is a
                great domain.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="fun-pronunciation">
          <AccordionTrigger>
            Is the name hard to pronounce?
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 text-foreground [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary-hover [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-0.5 [&_p]:text-muted-foreground">
              <p>
                TIE-gah. Like "tiger" without the "r" and with a Finnish
                accent. Two syllables. If you can say "sauna" correctly
                (SAW-nah, not SAW-nuh), you are already ahead of most people.
              </p>
              <p>
                Fun fact: every Finn we have met knows exactly what taiga
                means. Every non-Finn we have met asks. That is a pretty good
                conversation starter for a company that needs to explain a new
                category.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
}
